import { AuthGuard, CheckPermissionsModes } from "@elit-hair/elit-tools";
import {
    Button,
    DatePicker,
    ErrorState,
    Icon,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronLeft, TbChevronRight, TbPhoneCheck } from "react-icons/tb";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { useTeamCallsForDay } from "@/hooks/queries/call.queries";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { NotAuthorizedPage } from "@/pages/not-authorized/-index";
import { getTeamMemberIds } from "@/pages/performance/appointment-analytics/utils/appointment-analytics.utils";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { getHighestPriorityTeam, sortTeamsBySortOrder } from "@/utils/team.utils";

import { ReachabilityAgentTable } from "./components/reachability-agent-table";
import { ReachabilityHourlyChart } from "./components/reachability-hourly-chart";
import { ReachabilitySummaryCards } from "./components/reachability-summary-cards";
import {
    buildReachabilityAgentRows,
    buildReachabilityHourlySeries,
    buildReachabilitySummary,
} from "./utils/reachability-analysis.utils";

const ReachabilityAnalysisSearchSchema = z.object({
    team: z.string().optional(),
    date: z.string().optional(),
});

export type ReachabilityAnalysisSearch = z.infer<typeof ReachabilityAnalysisSearchSchema>;

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_REACHABILITY_ANALYSIS)({
    component: ReachabilityAnalysisPage,
    validateSearch: zodValidator(ReachabilityAnalysisSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "reachability_analysis",
                to: APP_ROUTES_TO.PERFORMANCE_REACHABILITY_ANALYSIS,
            },
            title: "reachability_analysis",
        },
        permissions: [
            PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE,
            PERMISSIONS.CALL_SVC_ADMIN_BASE,
            PERMISSIONS.CALL_REACHABILITY_ANALYSIS,
        ],
    },
});

function ReachabilityAnalysisPage() {
    return (
        <AuthGuard
            required={[
                PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE,
                PERMISSIONS.CALL_SVC_ADMIN_BASE,
                PERMISSIONS.CALL_REACHABILITY_ANALYSIS,
            ]}
            permissionMode={CheckPermissionsModes.ANY}
            fallback={<NotAuthorizedPage />}
        >
            <ReachabilityAnalysisContent />
        </AuthGuard>
    );
}

function ReachabilityAnalysisContent() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const selectedDate = useMemo(() => {
        if (!search.date) {
            return DateTime.now().startOf("day");
        }

        const parsed = DateTime.fromISO(search.date);
        return parsed.isValid ? parsed.startOf("day") : DateTime.now().startOf("day");
    }, [search.date]);

    const dateKey = selectedDate.toFormat("yyyy-MM-dd");

    const updateSearch = useCallback(
        (updates: Partial<ReachabilityAnalysisSearch>) => {
            navigate({
                to: Route.to,
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    const { data: teamsData, isLoading: isLoadingTeams } = useTeams();
    const accessibleTeams = useMemo(() => sortTeamsBySortOrder(teamsData?.data || []), [teamsData?.data]);

    const selectedTeam = useMemo(
        () => accessibleTeams.find((team) => team.id === search.team) ?? null,
        [accessibleTeams, search.team]
    );

    useEffect(() => {
        if (search.date) {
            return;
        }

        updateSearch({ date: DateTime.now().toFormat("yyyy-MM-dd") });
    }, [search.date, updateSearch]);

    useEffect(() => {
        if (search.team || accessibleTeams.length === 0) {
            return;
        }

        const defaultTeam = getHighestPriorityTeam(accessibleTeams);
        if (defaultTeam) {
            updateSearch({ team: defaultTeam.id });
        }
    }, [accessibleTeams, search.team, updateSearch]);

    const teamMemberIds = useMemo(() => (selectedTeam ? getTeamMemberIds(selectedTeam) : []), [selectedTeam]);

    const {
        data: calls = [],
        isLoading: isLoadingCalls,
        isError: isCallsError,
        error: callsError,
        refetch: refetchCalls,
    } = useTeamCallsForDay(dateKey, teamMemberIds, teamMemberIds.length > 0);

    const summary = useMemo(() => buildReachabilitySummary(calls), [calls]);
    const hourlySeries = useMemo(() => buildReachabilityHourlySeries(calls), [calls]);
    const agentRows = useMemo(() => buildReachabilityAgentRows(selectedTeam, calls), [selectedTeam, calls]);

    const handleDateChange = useCallback(
        (nextDate: DateTime) => {
            updateSearch({ date: nextDate.toFormat("yyyy-MM-dd") });
        },
        [updateSearch]
    );

    const isLoading = isLoadingTeams || (Boolean(selectedTeam) && isLoadingCalls);

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Icon icon={TbPhoneCheck} size={18} className="text-primary" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">{t("reachability_analysis")}</h1>
                        <p className="text-xs text-muted-foreground">{t("reachability_analysis_subtitle")}</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={selectedTeam?.id ?? ""}
                        onValueChange={(teamId) => updateSearch({ team: teamId })}
                        disabled={isLoadingTeams || accessibleTeams.length === 0}
                    >
                        <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder={t("select_team")} />
                        </SelectTrigger>
                        <SelectContent>
                            {accessibleTeams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="size-3 rounded-full border border-black/20 dark:border-white/30"
                                            style={{ backgroundColor: team.color }}
                                        />
                                        {team.name}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="inline-flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("previous_day")}
                            onClick={() => handleDateChange(selectedDate.minus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronLeft} size={16} />
                        </Button>
                        <DatePicker
                            date={selectedDate.toJSDate()}
                            onDateChange={(date) => {
                                if (!date) {
                                    return;
                                }

                                handleDateChange(DateTime.fromJSDate(date));
                            }}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("next_day")}
                            onClick={() => handleDateChange(selectedDate.plus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronRight} size={16} />
                        </Button>
                    </div>
                </div>
            </div>

            {!selectedTeam && !isLoadingTeams ? (
                <div className="rounded-xl border border-dashed border-border/60 px-4 py-10 text-center text-sm text-muted-foreground">
                    {t("select_team")}
                </div>
            ) : null}

            {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-16">
                    <Spinner />
                </div>
            ) : null}

            {isCallsError ? (
                <ErrorState
                    title={t("error")}
                    message={callsError instanceof Error ? callsError.message : t("connection_error")}
                    onRetry={() => {
                        void refetchCalls();
                    }}
                    retryLabel={t("try_again")}
                />
            ) : null}

            {selectedTeam && !isLoading && !isCallsError ? (
                <>
                    <ReachabilitySummaryCards summary={summary} />
                    <ReachabilityHourlyChart data={hourlySeries} />
                    <ReachabilityAgentTable rows={agentRows} />
                </>
            ) : null}
        </div>
    );
}
